import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useTRPC } from '@/lib/trpc'
import {
  useInvalidateSocial,
  useSocialWorkspace,
} from '@/social/social-provider'
import { useSocialText } from '@/social/use-social-text'
import { useMutation } from '@tanstack/react-query'
import { AtSign, Loader2 } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { toast } from 'sonner'

export function UsernameOnboarding() {
  const trpc = useTRPC()
  const text = useSocialText()
  const invalidateSocial = useInvalidateSocial()
  const { publishDataEvent } = useSocialWorkspace()
  const [username, setUsername] = useState('')
  const mutation = useMutation(trpc.socialRouter.setUsername.mutationOptions())
  const normalized = username.trim().toLowerCase()
  const valid = /^[a-z][a-z0-9_]{2,23}$/.test(normalized)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!valid || mutation.isPending)
      return
    try {
      await mutation.mutateAsync({ username: normalized })
      publishDataEvent('relationship.changed')
      await invalidateSocial()
      toast.success(text('social.username.success', 'Your username is ready.'))
    }
    catch {
      toast.error(
        text('social.username.error', 'That username is not available.'),
      )
    }
  }

  return (
    <div className="space-y-4 rounded-lg border bg-muted/20 p-4 sm:p-5">
      <div>
        <div className="mb-3 flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <AtSign className="size-5" />
        </div>
        <h3 className="font-semibold">
          {text('social.username.title', 'Choose your username')}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {text(
            'social.username.description',
            'Friends will use this public username to find you. Your email is never shown.',
          )}
        </p>
      </div>
      <form className="space-y-4" onSubmit={submit}>
        <div className="space-y-2">
          <Label htmlFor="social-username">
            {text('social.username.label', 'Username')}
          </Label>
          <div className="relative">
            <AtSign className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="social-username"
              value={username}
              onChange={event =>
                setUsername(event.target.value.toLowerCase())}
              placeholder={text('social.username.placeholder', 'your_username')}
              autoComplete="username"
              aria-describedby="social-username-rules"
              minLength={3}
              maxLength={24}
              className="pl-9"
            />
          </div>
          <p
            id="social-username-rules"
            className="text-xs text-muted-foreground"
          >
            {text(
              'social.username.rules',
              '3–24 characters. Start with a letter; use lowercase letters, numbers, or underscores.',
            )}
          </p>
          <p className="text-xs font-medium text-foreground">
            {text(
              'social.username.claimChangeAvailable',
              'After saving, you can change your username once later in Profile.',
            )}
          </p>
        </div>
        <Button
          type="submit"
          className="w-full"
          disabled={!valid || mutation.isPending}
        >
          {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
          {mutation.isPending
            ? text('social.username.saving', 'Saving…')
            : text('social.username.claim', 'Claim username')}
        </Button>
      </form>
    </div>
  )
}
